import { useState } from 'react'

import { editScheduleEditorActions } from '../actions'
import { useEditScheduleEditorOperations } from '../hooks'
import { buildOccupancyRows } from '../utils/occupancyHelpers'
import { validateEditScheduleForm } from '../utils/validateEditScheduleForm'
import { formatScheduleDate } from '../../schedule/utils/formatScheduleDate'
import { isWindowLesson } from '../../schedule/utils/lessonHelpers'
import { formatTeacherFull } from '../../../utils/formatTeacher'
import { useActions, useAppSelector } from '../../../store/hooks'
import { editSchedulePageSelector } from '../../../store/slices/editSchedulePage'
import { referencesSelector } from '../../../store/slices/references'
import { scheduleSelector } from '../../../store/slices/schedule'

export function useEditScheduleEditor({ item, lesson, isNew = false }) {
    const form = useAppSelector(editSchedulePageSelector.form)
    const isCreating = useAppSelector(editSchedulePageSelector.isCreating)
    const groups = useAppSelector(referencesSelector.groups)
    const teachers = useAppSelector(referencesSelector.teachers)
    const bells = useAppSelector(referencesSelector.bells)
    const occupancyData = useAppSelector(scheduleSelector.occupancyItems)
    const isEditorAudienceLoading = useAppSelector(referencesSelector.isEditorAudienceLoading)
    const groupOptions = useAppSelector(referencesSelector.groupOptions)
    const teacherOptions = useAppSelector(referencesSelector.teacherOptions)
    const subjectOptions = useAppSelector(referencesSelector.subjectOptions)
    const classTypeOptions = useAppSelector(referencesSelector.editorClassTypeOptions)
    const buildingOptions = useAppSelector(referencesSelector.buildingOptions)
    const buildingRoomOptions = useAppSelector(referencesSelector.editorRoomOptions)
    const bellOptions = useAppSelector(referencesSelector.bellOptions)

    const {
        updateFormField,
        updateFormFields,
    } = useActions(editScheduleEditorActions)

    const { handleSubmit, tryCloseEditor } = useEditScheduleEditorOperations()
    const [saveNotification, setSaveNotification] = useState(null)

    const handleChange = (event) => {
        const { name, value } = event.target
        updateFormField({ name, value })
    }

    const handleSelectChange = (name, value) => {
        updateFormField({
            name,
            value: value === undefined || value === null || value === '' ? '' : Number(value),
        })
    }

    const handleMultiSelectChange = (name, values) => {
        updateFormField({
            name,
            value: Array.isArray(values)
                ? values.map((v) => Number(v)).filter((v) => !Number.isNaN(v))
                : [],
        })
    }

    const handleBuildingChange = (value) => {
        updateFormFields({
            building_id: value === undefined || value === null || value === '' ? '' : Number(value),
            room_id: '',
        })
    }

    const handleGroupChange = (nextValue, multiple) => {
        if (multiple) {
            handleMultiSelectChange('group_id', nextValue)
            return
        }

        const num =
            nextValue === undefined || nextValue === null || nextValue === ''
                ? null
                : Number(nextValue)

        updateFormField({
            name: 'group_id',
            value: num === null || Number.isNaN(num) ? [] : [num],
        })
    }

    const occupancyRows = buildOccupancyRows(bells, occupancyData)
    const selectedOccupancy = occupancyRows.find(
        (row) => String(row.bellId) === String(form.bell_id)
    )

    const selectedTeacher = teachers.find(
        (teacher) => String(teacher.teacher_id) === String(form.teacher_id)
    )
    const selectedTeacherName = selectedTeacher
        ? formatTeacherFull(selectedTeacher, 'преподаватель')
        : 'преподаватель'

    const selectedIds = Array.isArray(form.group_id) ? form.group_id : []
    const selectedGroupNames = selectedIds.length === 0
        ? 'группы не выбраны'
        : selectedIds
            .map((groupId) => groups.find((group) => String(group.group_id) === String(groupId)))
            .map((group, index) => group?.name || String(selectedIds[index]))
            .join(', ')

    const showOccupancy = Boolean(
        form.bell_id
        && form.teacher_id
        && Array.isArray(form.group_id)
        && form.group_id.length > 0
        && form.room_id
    )

    const isWindow = !isNew && item && lesson && isWindowLesson(item, lesson)

    const handleSave = () => {
        const validationIssues = validateEditScheduleForm(form, {
            isCreating: isNew || isCreating,
        })

        if (validationIssues.length > 0) {
            setSaveNotification({
                key: Date.now(),
                issues: validationIssues,
            })
            return
        }

        handleSubmit(form)
    }

    return {
        form,
        isNew: isNew || isCreating,
        isWindow,
        item,
        lesson,
        groupOptions,
        teacherOptions,
        subjectOptions,
        classTypeOptions,
        buildingOptions,
        buildingRoomOptions,
        bellOptions,
        isEditorAudienceLoading,
        occupancyRows,
        selectedOccupancy,
        selectedTeacherName,
        selectedGroupNames,
        selectedIds,
        groups,
        showOccupancy,
        formatScheduleDate,
        handleChange,
        handleSelectChange,
        handleBuildingChange,
        handleGroupChange,
        handleSubmit: handleSave,
        tryCloseEditor,
        saveNotification,
    }
}
